import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ClipboardCheck, FileText, Inbox, Settings, ShieldCheck, Tractor, UserCheck } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Tabs } from '../../components/ui/Tabs';
import inductionService from '../../services/induction';
import { useInductionSeats } from '../../components/induction/useInductionSeats';
import { InductionAccessDenied, InductionSkeleton } from '../../components/induction/InductionGuards';
import RegisterPanel from '../../components/induction/RegisterPanel';
import EquipmentInductionsPanel from '../../components/induction/EquipmentInductionsPanel';
import RequestsPanel from '../../components/induction/RequestsPanel';
import PolicyPackPanel from '../../components/induction/PolicyPackPanel';
import InductionSettingsPanel from '../../components/induction/InductionSettingsPanel';

type TabKey = 'register' | 'equipment' | 'requests' | 'policies' | 'settings';

/**
 * /inductions — who is signed off on which machines, the inductions
 * themselves, sign-off requests, the policy pack and enforcement. For
 * assessors (Farm Jobs supervisor/admin, Vehicles creator/admin) and HR.
 */
export default function InductionsHub() {
  const [params, setParams] = useSearchParams();
  const { loading, hasAccess, canAssess, canManagePack } = useInductionSeats();
  const [enforce, setEnforce] = useState<boolean | null>(null);

  useEffect(() => {
    if (!hasAccess) return;
    inductionService.access()
      .then(a => setEnforce(a ? a.enforce_equipment : null))
      .catch(() => setEnforce(null));
  }, [hasAccess]);

  const tabs: { key: TabKey; label: string; icon: ReactNode }[] = [
    { key: 'register', label: 'Register', icon: <ClipboardCheck className="h-4 w-4" /> },
    { key: 'equipment', label: 'Equipment', icon: <Tractor className="h-4 w-4" /> },
    ...(canAssess ? [{ key: 'requests' as const, label: 'Requests', icon: <Inbox className="h-4 w-4" /> }] : []),
    ...(canManagePack ? [{ key: 'policies' as const, label: 'Policies', icon: <FileText className="h-4 w-4" /> }] : []),
    ...(canAssess ? [{ key: 'settings' as const, label: 'Settings', icon: <Settings className="h-4 w-4" /> }] : []),
  ];
  const tabParam = params.get('tab') as TabKey | null;
  const tab: TabKey = tabs.some(t => t.key === tabParam) ? (tabParam as TabKey) : 'register';

  if (loading) return <InductionSkeleton />;
  if (!hasAccess) return <InductionAccessDenied />;

  return (
    <>
      <PageHeader
        title="Inductions"
        subtitle="Who is signed off on which machines, and the induction pack"
        icon={<ShieldCheck className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Link
              to="/my-inductions"
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              <UserCheck className="h-4 w-4" /> My inductions
            </Link>
          </div>
        }
      />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs
          tabs={tabs}
          active={tab}
          onChange={k => setParams(k === 'register' ? {} : { tab: k }, { replace: true })}
          className="mb-4"
        />
        <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
          {tab === 'register' && <RegisterPanel canAssess={canAssess} canManagePack={canManagePack} enforce={enforce} />}
          {tab === 'equipment' && <EquipmentInductionsPanel canAssess={canAssess} />}
          {tab === 'requests' && <RequestsPanel />}
          {tab === 'policies' && <PolicyPackPanel />}
          {tab === 'settings' && <InductionSettingsPanel enforce={enforce} onChanged={setEnforce} />}
        </div>
      </div>
    </>
  );
}
